// src/components/dashboard/ai_flow/resumeTemplateStyles.js
import executivePremiumCss from "./PremiumResume/ExecutivePremiumTemplate.css?raw";
// ========================================================================
//  1. DEFINE CSS STYLES FOR EACH TEMPLATE
// ========================================================================

// --- TEMPLATE 1: CLASSIC ---
// A traditional, serif-font resume style.
const classicStyles = `
  /* --- STYLES FOR BOTH EDITOR AND EXPORT --- */
  .template-classic .ProseMirror,
  .resume-template-container.template-classic {
    font-family: 'Georgia', 'Times New Roman', serif;
    font-size: 11pt;
    line-height: 1.4;
    color: #1a202c;
    padding: 40px 50px;
    padding-top: 0px;
  }

  /* Main Name Header */
  .template-classic .ProseMirror h1,
  .resume-template-container.template-classic h1 {
    font-size: 24pt; 
    font-weight: bold; 
    text-align: center; 
    margin-bottom: 8px; 
    border-bottom: none;
    letter-spacing: 1px;
  }

  /* Contact Info - Modern Centered Layout with Icons */
  .template-classic .ProseMirror h1 + p,
  .resume-template-container.template-classic h1 + p,
  .template-classic .ProseMirror .contact-info,
  .resume-template-container.template-classic .contact-info {
    text-align: center;
    font-size: 10pt;
    color: #4b5563;
    margin: 0 0 1.5em 0;
    padding-bottom: 1em;
    border-bottom: 1px solid #e5e7eb;
    display: flex;
    justify-content: center;
    align-items: center;
    flex-wrap: wrap;
    gap: 0.5em;
  }

  /* Contact Info Items with Separators */
  .template-classic .ProseMirror .contact-info > *:not(:last-child)::after,
  .resume-template-container.template-classic .contact-info > *:not(:last-child)::after {
    content: "•";
    margin-left: 0.5em;
    color: #9ca3af;
    font-weight: bold;
  }

  /* If contact info is in a single paragraph, create visual separators */
  .template-classic .ProseMirror h1 + p,
  .resume-template-container.template-classic h1 + p {
    position: relative;
  }

  /* Section Headers */
  .template-classic .ProseMirror h3,
  .resume-template-container.template-classic h3 {
    font-size: 12pt; 
    font-weight: bold; 
    margin-top: 1.5em; 
    margin-bottom: 0.5em; 
    padding-bottom: 2px; 
    border-bottom: 1px solid #333;
    color: #1a202c;
  }

  /* Regular Paragraphs */
  .template-classic .ProseMirror p,
  .resume-template-container.template-classic p {
    margin: 0.4em 0;
  }

  /* Bullet Lists - Proper Styling */
  .template-classic .ProseMirror ul,
  .resume-template-container.template-classic ul {
    padding-left: 20px;
    list-style-position: outside;
    list-style-type: disc;
    margin: 0.5em 0;
  }

  .template-classic .ProseMirror li,
  .resume-template-container.template-classic li {
    margin-bottom: 0.3em;
    padding-left: 5px;
  }

  /* Nested Lists */
  .template-classic .ProseMirror ul ul,
  .resume-template-container.template-classic ul ul {
    list-style-type: circle;
    margin-top: 0.2em;
    margin-bottom: 0.2em;
  }

  /* Job Headers - Modern Layout */
  .template-classic .ProseMirror .job-header,
  .resume-template-container.template-classic .job-header,
  .template-classic .ProseMirror .edu-header,
  .resume-template-container.template-classic .edu-header {
    margin-top: 1em;
    margin-bottom: 0.5em;
  }

  .template-classic .ProseMirror .job-company-title,
  .resume-template-container.template-classic .job-company-title,
  .template-classic .ProseMirror .edu-uni-degree,
  .resume-template-container.template-classic .edu-uni-degree {
    font-weight: bold;
    color: #1a202c;
  }

  .template-classic .ProseMirror .job-dates-location,
  .resume-template-container.template-classic .job-dates-location,
  .template-classic .ProseMirror .edu-dates-location,
  .resume-template-container.template-classic .edu-dates-location {
    font-style: italic;
    color: #6b7280;
    margin-top: 0.2em;
  }

  /* Links */
  .template-classic .ProseMirror a,
  .resume-template-container.template-classic a {
    color: #1971c2;
    text-decoration: underline;
    text-decoration-color: rgba(25, 113, 194, 0.3);
  }

  .template-classic .ProseMirror a:hover,
  .resume-template-container.template-classic a:hover {
    text-decoration-color: rgba(25, 113, 194, 0.8);
  }

  /* Strong/Bold Text */
  .template-classic .ProseMirror strong,
  .resume-template-container.template-classic strong {
    font-weight: 600;
    color: #111827;
  }

  /* Ordered Lists */
  .template-classic .ProseMirror ol,
  .resume-template-container.template-classic ol {
    padding-left: 20px;
    list-style-position: outside;
    list-style-type: decimal;
    margin: 0.5em 0;
  }

  /* --- STYLES FOR EXPORT ONLY --- */
  /* This adds the uppercase transform ONLY to the final PDF/DOC */
  .resume-template-container.template-classic h3 {
    text-transform: uppercase;
    letter-spacing: 2px;
  }

  /* Better spacing for print */
  .resume-template-container.template-classic {
    line-height: 1.3;
  }

  /* Ensure proper bullet points in export */
  .resume-template-container.template-classic ul {
    list-style-type: disc !important;
  }

  .resume-template-container.template-classic ul ul {
    list-style-type: circle !important;
  }
    @media only screen and (max-width: 767px) {
  .template-classic .ProseMirror,
  .resume-template-container.template-classic,
  .template-modern .ProseMirror,
  .resume-template-container.template-modern,
  .template-executive .ProseMirror,
  .resume-template-container.template-executive {
    padding: 0px; /* Adjusted padding for mobile */
  }
`;

// --- TEMPLATE 2: MODERN ---
const modernStyles = `
  /* --- STYLES FOR BOTH EDITOR AND EXPORT --- */
  .template-modern .ProseMirror,
  .resume-template-container.template-modern {
    font-family: 'Helvetica', 'Arial', sans-serif;
    font-size: 10.5pt;
    line-height: 1.5;
    color: #333;
    padding: 40px 50px;
    padding-top: 0px;
  }

  /* Main Name Header */
  .template-modern .ProseMirror h1,
  .resume-template-container.template-modern h1 {
    font-size: 28pt; 
    font-weight: 600; 
    text-align: center; 
    margin-bottom: 8px; 
    color: rgb(6 95 70); 
    border-bottom: none; 
    padding-bottom: 0;
    letter-spacing: 1px;
  }

  /* Contact Info - Modern Centered Layout with Separators */
  .template-modern .ProseMirror h1 + p,
  .resume-template-container.template-modern h1 + p,
  .template-modern .ProseMirror .contact-info,
  .resume-template-container.template-modern .contact-info {
    text-align: center;
    font-size: 10pt;
    color: #4b5563;
    margin: 0 0 1.5em 0;
    padding-bottom: 1em;
    border-bottom: 1px solid #e5e7eb;
    display: flex;
    justify-content: center;
    align-items: center;
    flex-wrap: wrap;
    gap: 0.5em;
  }

  /* Contact Info Items with Separators */
  .template-modern .ProseMirror .contact-info > *:not(:last-child)::after,
  .resume-template-container.template-modern .contact-info > *:not(:last-child)::after {
    content: "•";
    margin-left: 0.5em;
    color: #9ca3af;
    font-weight: bold;
  }

  /* Section Headers */
  .template-modern .ProseMirror h3,
  .resume-template-container.template-modern h3 {
    font-size: 11pt; 
    font-weight: 600; 
    letter-spacing: 1px; 
    color: rgb(6 95 70); 
    margin-top: 1.8em; 
    margin-bottom: 0.6em; 
    padding-bottom: 4px; 
    border-bottom: 1px solid #e5e7eb;
  }

  /* Regular Paragraphs */
  .template-modern .ProseMirror p,
  .resume-template-container.template-modern p { 
    margin: 0.3em 0; 
  }

  /* Bullet Lists - Proper Styling */
  .template-modern .ProseMirror ul,
  .resume-template-container.template-modern ul { 
    padding-left: 18px; 
    list-style-position: outside;
    list-style-type: disc;
    margin: 0.5em 0;
  }

  .template-modern .ProseMirror li,
  .resume-template-container.template-modern li {
    margin-bottom: 0.3em;
    padding-left: 5px;
  }

  /* Nested Lists */
  .template-modern .ProseMirror ul ul,
  .resume-template-container.template-modern ul ul {
    list-style-type: circle;
    margin-top: 0.2em;
    margin-bottom: 0.2em;
  }

  /* Ordered Lists */
  .template-modern .ProseMirror ol,
  .resume-template-container.template-modern ol {
    padding-left: 18px;
    list-style-position: outside;
    list-style-type: decimal;
    margin: 0.5em 0;
  }

  /* Links */
  .template-modern .ProseMirror a,
  .resume-template-container.template-modern a { 
    color: rgb(6 95 70); 
    text-decoration: none; 
    font-weight: 500;
    transition: text-decoration 0.2s ease-in-out;
  }

  .template-modern .ProseMirror a:hover,
  .resume-template-container.template-modern a:hover { 
    text-decoration: underline; 
  }

  /* Strong/Bold Text */
  .template-modern .ProseMirror strong,
  .resume-template-container.template-modern strong {
    font-weight: 600;
    color: #1f2937;
  }

  /* Job Headers - Clean Layout */
  .template-modern .ProseMirror .job-header,
  .resume-template-container.template-modern .job-header,
  .template-modern .ProseMirror .edu-header,
  .resume-template-container.template-modern .edu-header {
    margin-top: 1em;
    margin-bottom: 0.5em;
  }

  .template-modern .ProseMirror .job-company-title,
  .resume-template-container.template-modern .job-company-title,
  .template-modern .ProseMirror .edu-uni-degree,
  .resume-template-container.template-modern .edu-uni-degree {
    font-weight: 600;
    color: #1f2937;
    font-size: 11pt;
  }

  .template-modern .ProseMirror .job-dates-location,
  .resume-template-container.template-modern .job-dates-location,
  .template-modern .ProseMirror .edu-dates-location,
  .resume-template-container.template-modern .edu-dates-location {
    font-style: italic;
    color: #6b7280;
    margin-top: 0.2em;
    font-size: 10pt;
  }
  
  /* --- STYLES FOR EXPORT ONLY --- */
  /* This adds the uppercase transform ONLY to the final PDF/DOC */
  .resume-template-container.template-modern h3 {
    text-transform: uppercase;
  }

  /* Ensure proper bullet points in export */
  .resume-template-container.template-modern ul {
    list-style-type: disc !important;
  }

  .resume-template-container.template-modern ul ul {
    list-style-type: circle !important;
  }
`;

// --- TEMPLATE 3: EXECUTIVE ---
// A single-column version of the professional two-tone resume.
const executiveStyles = `
  /* --- STYLES FOR BOTH EDITOR AND EXPORT --- */
  .template-executive .ProseMirror,
  .resume-template-container.template-executive {
    font-family: 'Helvetica Neue', Helvetica, Arial, sans-serif;
    font-size: 10pt;
    line-height: 1.5;
    color: #333333;
    padding: 30px 40px;
    text-align: left;
    padding-top: 0px;
  }

  /* MAIN NAME HEADER */
  .template-executive .ProseMirror h1,
  .resume-template-container.template-executive h1 {
    font-size: 32pt;
    font-weight: 700;
    letter-spacing: 4px;
    text-align: center;
    color: #2d2d2d;
    margin: 0;
    padding: 0;
    line-height: 1.1;
    border-bottom: none;
  }

  /* Subtitle/Contact Info - Centered with Separators */
  .template-executive .ProseMirror h1 + p,
  .resume-template-container.template-executive h1 + p,
  .template-executive .ProseMirror .contact-info,
  .resume-template-container.template-executive .contact-info {
    font-size: 10pt;
    letter-spacing: 3px;
    text-align: center;
    margin: 4px 0 10px 0;
    padding-bottom: 12px;
    border-bottom: 1px solid #cccccc;
    display: flex;
    justify-content: center;
    align-items: center;
    flex-wrap: wrap;
    gap: 0.5em;
  }

  /* Contact Info Items with Separators */
  .template-executive .ProseMirror .contact-info > *:not(:last-child)::after,
  .resume-template-container.template-executive .contact-info > *:not(:last-child)::after {
    content: "•";
    margin-left: 0.5em;
    color: #888888;
    font-weight: bold;
  }

  /* SECTION HEADERS (Profile, Experience, Education, etc.) */
  .template-executive .ProseMirror h2,
  .resume-template-container.template-executive h2 {
    font-size: 12pt;
    font-weight: 600;
    letter-spacing: 2px;
    color: #333333;
    margin-top: 20px;
    margin-bottom: 8px;
    padding-bottom: 5px;
    border-bottom: 2px solid #333333;
  }

  /* Job Titles / Degree Titles */
  .template-executive .ProseMirror h3,
  .resume-template-container.template-executive h3 {
    font-size: 11pt;
    font-weight: 700;
    color: #1a1a1a;
    margin-top: 12px;
    margin-bottom: 2px;
  }

  /* Company Name / School Name */
  .template-executive .ProseMirror h3 + p,
  .resume-template-container.template-executive h3 + p {
    font-size: 10pt;
    font-style: italic;
    color: #555555;
    margin-top: 0;
    margin-bottom: 8px;
  }

  /* General paragraphs */
  .template-executive .ProseMirror p,
  .resume-template-container.template-executive p {
    margin: 0.2em 0;
  }

  /* Bullet Lists - Proper Styling */
  .template-executive .ProseMirror ul,
  .resume-template-container.template-executive ul {
    list-style-position: outside;
    list-style-type: disc;
    padding-left: 20px;
    margin-top: 4px;
    margin-bottom: 0.5em;
  }

  .template-executive .ProseMirror li,
  .resume-template-container.template-executive li {
    padding-left: 5px;
    margin-bottom: 6px;
  }

  /* Nested Lists */
  .template-executive .ProseMirror ul ul,
  .resume-template-container.template-executive ul ul {
    list-style-type: circle;
    margin-top: 0.2em;
    margin-bottom: 0.2em;
  }

  /* Ordered Lists */
  .template-executive .ProseMirror ol,
  .resume-template-container.template-executive ol {
    list-style-position: outside;
    list-style-type: decimal;
    padding-left: 20px;
    margin-top: 4px;
    margin-bottom: 0.5em;
  }

  /* Links */
  .template-executive .ProseMirror a,
  .resume-template-container.template-executive a {
    color: rgb(6 95 70);
    text-decoration: none;
    font-weight: 500;
    transition: text-decoration 0.2s ease-in-out;
  }

  .template-executive .ProseMirror a:hover,
  .resume-template-container.template-executive a:hover {
    text-decoration: underline;
  }

  /* Strong/Bold Text */
  .template-executive .ProseMirror strong,
  .resume-template-container.template-executive strong {
    font-weight: 700;
    color: #1a1a1a;
  }

  /* Job Headers - Executive Layout */
  .template-executive .ProseMirror .job-header,
  .resume-template-container.template-executive .job-header,
  .template-executive .ProseMirror .edu-header,
  .resume-template-container.template-executive .edu-header {
    margin-top: 1em;
    margin-bottom: 0.5em;
  }

  .template-executive .ProseMirror .job-company-title,
  .resume-template-container.template-executive .job-company-title,
  .template-executive .ProseMirror .edu-uni-degree,
  .resume-template-container.template-executive .edu-uni-degree {
    font-weight: 700;
    color: #1a1a1a;
    font-size: 11pt;
  }

  .template-executive .ProseMirror .job-dates-location,
  .resume-template-container.template-executive .job-dates-location,
  .template-executive .ProseMirror .edu-dates-location,
  .resume-template-container.template-executive .edu-dates-location {
    font-style: italic;
    color: #555555;
    margin-top: 0.2em;
    font-size: 10pt;
  }

  /* Special class for contact info block paragraphs */
  .template-executive .ProseMirror .contact-info p,
  .resume-template-container.template-executive .contact-info p {
    margin: 2px 0;
  }

  /* --- STYLES FOR EXPORT ONLY --- */
  .resume-template-container.template-executive h1,
  .resume-template-container.template-executive h1 + p,
  .resume-template-container.template-executive h2 {
    text-transform: uppercase;
  }

  /* Ensure proper bullet points in export */
  .resume-template-container.template-executive ul {
    list-style-type: disc !important;
  }

  .resume-template-container.template-executive ul ul {
    list-style-type: circle !important;
  }
`;

// --- TEMPLATE 4: PROFESSIONAL ---
// const professionalStyles = `
//   /* --- STYLES FOR BOTH EDITOR AND EXPORT --- */
//   .template-professional .ProseMirror,
//   .resume-template-container.template-professional {
//     font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Helvetica Neue', Arial, sans-serif;
//     font-size: 11pt;
//     line-height: 1.6;
//     color: #333;
//     background: white;
//     padding: 0.75in;
//   }

//   /* Main Name Header */
//   .template-professional .ProseMirror h1,
//   .resume-template-container.template-professional h1 {
//     font-size: 28pt;
//     font-weight: 700;
//     color: #2c3e50;
//     text-transform: uppercase;
//     letter-spacing: 1px;
//     margin: 0 0 8px 0;
//     text-align: center;
//     border-bottom: none;
//   }

//   /* Tagline */
//   .template-professional .ProseMirror .tagline,
//   .resume-template-container.template-professional .tagline {
//     font-size: 14pt;
//     color: #555;
//     font-style: italic;
//     margin: 0 0 16px 0;
//     text-align: center;
//   }

//   /* Contact Info - Centered with separators */
//   .template-professional .ProseMirror .contact-info,
//   .resume-template-container.template-professional .contact-info {
//     display: flex;
//     justify-content: center;
//     flex-wrap: wrap;
//     gap: 20px;
//     font-size: 10pt;
//     color: #666;
//     text-align: center;
//     margin-bottom: 20px;
//     padding-bottom: 20px;
//     border-bottom: 2px solid #e0e0e0;
//   }

//   .template-professional .ProseMirror .contact-info span,
//   .resume-template-container.template-professional .contact-info span {
//     display: flex;
//     align-items: center;
//     gap: 4px;
//   }

//   /* Section Headers - Professional style */
//   .template-professional .ProseMirror h2,
//   .resume-template-container.template-professional h2 {
//     font-size: 14pt;
//     font-weight: 700;
//     color: #2c3e50;
//     text-transform: uppercase;
//     letter-spacing: 1px;
//     margin: 24px 0 12px 0;
//     padding-bottom: 4px;
//     border-bottom: 2px solid #2c3e50;
//   }

//   /* Subsection Headers (Job titles, etc) */
//   .template-professional .ProseMirror h3,
//   .resume-template-container.template-professional h3 {
//     font-size: 12pt;
//     font-weight: 600;
//     color: #2c3e50;
//     margin: 16px 0 4px 0;
//   }

//   /* Regular Paragraphs */
//   .template-professional .ProseMirror p,
//   .resume-template-container.template-professional p {
//     margin: 0.4em 0;
//     text-align: justify;
//   }

//   /* Profile Section */
//   .template-professional .ProseMirror .profile-section p,
//   .resume-template-container.template-professional .profile-section p {
//     text-align: justify;
//     font-size: 11pt;
//     line-height: 1.6;
//     color: #444;
//     margin: 0;
//   }

//   /* Experience Items */
//   .template-professional .ProseMirror .experience-item,
//   .resume-template-container.template-professional .experience-item {
//     margin-bottom: 20px;
//   }

//   /* Company Info */
//   .template-professional .ProseMirror .company-info,
//   .resume-template-container.template-professional .company-info {
//     font-size: 11pt;
//     color: #666;
//     margin: 0 0 8px 0;
//     font-style: italic;
//   }

//   /* Bullet Lists */
//   .template-professional .ProseMirror ul,
//   .resume-template-container.template-professional ul {
//     margin: 0;
//     padding-left: 20px;
//     list-style-type: disc;
//   }

//   .template-professional .ProseMirror li,
//   .resume-template-container.template-professional li {
//     font-size: 11pt;
//     color: #444;
//     margin-bottom: 6px;
//     line-height: 1.5;
//   }

//   /* Education Items */
//   .template-professional .ProseMirror .education-item,
//   .resume-template-container.template-professional .education-item {
//     margin-bottom: 16px;
//   }

//   .template-professional .ProseMirror .education-item h3,
//   .resume-template-container.template-professional .education-item h3 {
//     font-size: 12pt;
//     font-weight: 600;
//     color: #2c3e50;
//     margin: 0 0 4px 0;
//   }

//   .template-professional .ProseMirror .education-item p,
//   .resume-template-container.template-professional .education-item p {
//     font-size: 11pt;
//     color: #666;
//     margin: 0;
//     font-style: italic;
//   }

//   /* Skills List */
//   .template-professional .ProseMirror .skills-list,
//   .resume-template-container.template-professional .skills-list {
//     margin: 0;
//     padding-left: 20px;
//     list-style-type: disc;
//   }

//   .template-professional .ProseMirror .skills-list li,
//   .resume-template-container.template-professional .skills-list li {
//     font-size: 11pt;
//     color: #444;
//     margin-bottom: 6px;
//     line-height: 1.5;
//   }

//   /* Two-Column Layout Support */
//   .template-professional .ProseMirror .two-column-layout,
//   .resume-template-container.template-professional .two-column-layout {
//     display: grid;
//     grid-template-columns: 2fr 1fr;
//     gap: 0.5in;
//     margin-top: 0.25in;
//   }

//   .template-professional .ProseMirror .main-column,
//   .resume-template-container.template-professional .main-column {
//     padding-right: 0.25in;
//   }

//   .template-professional .ProseMirror .side-column,
//   .resume-template-container.template-professional .side-column {
//     padding-left: 0.25in;
//     border-left: 1px solid #e0e0e0;
//   }

//   .template-professional .ProseMirror .side-column h2,
//   .resume-template-container.template-professional .side-column h2 {
//     font-size: 12pt;
//     border-bottom-width: 1px;
//   }

//   /* Links */
//   .template-professional .ProseMirror a,
//   .resume-template-container.template-professional a {
//     color: #2c3e50;
//     text-decoration: none;
//     font-weight: 500;
//   }

//   .template-professional .ProseMirror a:hover,
//   .resume-template-container.template-professional a:hover {
//     text-decoration: underline;
//   }

//   /* Strong/Bold Text */
//   .template-professional .ProseMirror strong,
//   .resume-template-container.template-professional strong {
//     font-weight: 600;
//     color: #2c3e50;
//   }

//   /* Notable Accolades Section */
//   .template-professional .ProseMirror .accolades-section,
//   .resume-template-container.template-professional .accolades-section {
//     background: #f8f9fa;
//     padding: 12px 16px;
//     border-radius: 4px;
//     margin-top: 16px;
//   }

//   .template-professional .ProseMirror .accolades-section h3,
//   .resume-template-container.template-professional .accolades-section h3 {
//     font-size: 11pt;
//     font-weight: 600;
//     color: #2c3e50;
//     margin: 0 0 8px 0;
//   }

//   .template-professional .ProseMirror .accolades-section ul,
//   .resume-template-container.template-professional .accolades-section ul {
//     margin: 0;
//     padding-left: 16px;
//     list-style-type: none;
//   }

//   .template-professional .ProseMirror .accolades-section li,
//   .resume-template-container.template-professional .accolades-section li {
//     font-size: 10pt;
//     color: #555;
//     margin-bottom: 4px;
//     position: relative;
//     padding-left: 16px;
//   }

//   .template-professional .ProseMirror .accolades-section li:before,
//   .resume-template-container.template-professional .accolades-section li:before {
//     content: "▸";
//     position: absolute;
//     left: 0;
//     color: #2c3e50;
//   }

//   /* Print Styles */
//   @media print {
//     .resume-template-container.template-professional {
//       padding: 0.5in;
//       font-size: 10pt;
//     }

//     .resume-template-container.template-professional h1 {
//       font-size: 24pt;
//     }

//     .resume-template-container.template-professional h2 {
//       font-size: 12pt;
//       page-break-after: avoid;
//     }

//     .resume-template-container.template-professional .experience-item,
//     .resume-template-container.template-professional .education-item {
//       page-break-inside: avoid;
//     }

//     .resume-template-container.template-professional .two-column-layout {
//       page-break-inside: avoid;
//     }
//   }
// `;

// ========================================================================
//  2. ADD GLOBAL SPACING FIXES
// ========================================================================

/* Add these styles to your resumeTemplateStyles.js for better spacing control */

/* Global spacing improvements for all templates */
const spacingFixes = `
  /* Reduce excessive margins that cause white space */
  .template-classic .ProseMirror h3,
  .template-modern .ProseMirror h3,
  .template-executive .ProseMirror h3,
  .template-professional .ProseMirror h3 {
    margin-top: 1.2em; /* Reduced from 1.5em/1.8em */
    margin-bottom: 0.4em; /* Reduced from 0.5em/0.6em */
  }

  /* Tighter paragraph spacing */
  .template-classic .ProseMirror p,
  .template-modern .ProseMirror p,
  .template-executive .ProseMirror p,
  .template-professional .ProseMirror p {
    margin: 0.3em 0; /* Reduced from 0.4em */
  }

  /* Optimize list spacing */
  .template-classic .ProseMirror ul,
  .template-modern .ProseMirror ul,
  .template-executive .ProseMirror ul,
  .template-professional .ProseMirror ul {
    margin: 0.4em 0; /* Reduced from 0.5em */
    padding-left: 18px; /* Slightly less indent */
  }

  .template-classic .ProseMirror li,
  .template-modern .ProseMirror li,
  .template-executive .ProseMirror li,
  .template-professional .ProseMirror li {
    margin-bottom: 0.25em; /* Reduced from 0.3em */
    line-height: 1.4; /* Ensure consistent line height */
  }

  /* Skills section - ensure it stays compact */
  .ProseMirror h3:has(+ p:contains("Technical")) {
    margin-bottom: 0.3em;
  }

  /* Prevent orphaned headers */
  .ProseMirror h3 {
    break-after: avoid;
    page-break-after: avoid;
  }

  /* Keep job headers together with content */
  .job-header,
  .edu-header {
    break-after: avoid;
    page-break-after: avoid;
    margin-top: 0.8em;
    margin-bottom: 0.3em;
  }

  /* Ensure at least 2 lines of content follow headers */
  .ProseMirror h3 + * {
    break-before: avoid;
    page-break-before: avoid;
  }

  /* Compact contact info */
  .contact-info {
    margin-bottom: 0.8em !important; /* Reduced spacing */
    padding-bottom: 0.8em !important;
  }

  /* First h3 (usually Professional Summary) needs less top margin */
  .ProseMirror h3:first-of-type {
    margin-top: 0.8em;
  }

  /* Optimize PDF rendering */
  @media print {
    .ProseMirror {
      font-size: 10.5pt; /* Slightly smaller for better fit */
    }
    
    .ProseMirror h1 {
      font-size: 22pt; /* Slightly smaller */
      margin-bottom: 6px;
    }
    
    .ProseMirror h3 {
      font-size: 11.5pt; /* Slightly smaller */
    }
  }
`;

// ========================================================================
//  3. DEFINE AND EXPORT THE TEMPLATE DATA
//     This array is the single source of truth for the entire app.
// ========================================================================

export const templates = [
  {
    id: "classic",
    name: "Classic Professional",
    styles: classicStyles + spacingFixes,
    type: "basic",
  },
  // {
  //   id: "modern",
  //   name: "Modern Sans-Serif",
  //   styles: modernStyles + spacingFixes,
  //   type: "basic",
  // },
  // {
  //   id: "executive",
  //   name: "Executive Two-Tone",
  //   styles: executiveStyles + spacingFixes,
  //   type: "basic",
  // },
  // {
  //   id: "professional",
  //   name: "Professional",
  //   styles: professionalStyles + spacingFixes,
  //   type: "premium", // This matches your PDF style
  // },
  {
    id: "executive-premium-tae",
    name: "Executive Two-Column (Premium)",
    type: "premium", // This is the crucial part
    component: "ExecutivePremiumTemplate", // This tells the PDF renderer which component to use
    styles: "", // Premium templates do not need inline styles here
  },
  // {
  //   id: "executive-premium-tae-2",
  //   name: "Executive Premium",
  //   type: "premium",
  //   styles: executivePremiumCss,
  // },
];
